import { DiscountStatusEnum, IDiscount } from '@monetize/types/app';
import {
  FilterType,
  FilterTypeEqual,
  FilterTypeOperator,
  RenderTableFilterOptionProps,
} from '@monetize/types/common';
import { useQuery } from '@tanstack/react-query';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { getAccountById } from '../../../api/accountsService';
import {
  useGetListData,
  usePerformEntityAction,
} from '../../../api/queryUtils';
import {
  MFlex,
  MHStack,
  MPageContainer,
  MPageSearchInput,
  MSwitch,
  MText,
} from '../../../components/Monetize';
import {
  AccountIdTableFilterOptionContent,
  MDataTableActions,
  MDataTableFilter,
} from '../../../components/Monetize/DataTable';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import {
  ExternalFilterDef,
  useExternalFilters,
} from '../../../components/Monetize/DataTableNew/useExternalFilters';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { ROUTES } from '../../../constants';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import {
  tableFilterSelector,
  tablePagerSelector,
  tableSearchTermSelector,
} from '../../../store/global.store';
import {
  FilterStateKeys,
  getDefaultFilter,
  getDefaultPager,
} from '../../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
  transformTableFilterValue,
} from '../../../utils';
import { navigateOrOpenNewWindow } from '../../../utils/misc';
import PageHeader from '../components/PageHeader';
import TableHeader from '../components/TableHeader';
import {
  DISCOUNT_LIST_CATALOG,
  DiscountListColumnContext,
} from './discountList.columns';

const renderAccountIdToggleOptionContent = ({
  filter,
  filterOption,
  handleFilterChange,
}: RenderTableFilterOptionProps) => {
  const internalFilter = filter as FilterTypeEqual;

  return (
    <MFlex alignItems="center" my="2" justifyContent="space-between">
      <MText mr="4">Show Custom Discounts</MText>
      <MSwitch
        size="md"
        isChecked={internalFilter?.value === filterOption.options?.falseValue}
        onChange={(val) => {
          handleFilterChange(
            val.target.checked
              ? filterOption.options?.falseValue!
              : filterOption.options?.trueValue!,
            filterOption,
          );
        }}
      />
    </MFlex>
  );
};

const DiscountListPage: FC = () => {
  const navigate = useNavigate();
  const tableRef = useRef<DataTableRef>(null);

  const [persistedFilters, persistTableFilter] = useAtom(
    tableFilterSelector(FilterStateKeys.DISCOUNT_LIST),
  );

  const [pager, setPager] = useAtom(
    tablePagerSelector(FilterStateKeys.DISCOUNT_LIST),
  );

  const [searchTerm, setSearchTerm] = useAtom(
    tableSearchTermSelector(FilterStateKeys.DISCOUNT_LIST),
  );

  const [filters, setFilters] = useState<FilterType[]>(persistedFilters);
  const [searchKey] = useState<string>('name');

  const filterParams = useMemo(
    () =>
      buildFilterParamsRequestObject(
        transformTableFilterValue(filters),
        searchTerm,
        searchKey,
      ),
    [filters, searchTerm, searchKey],
  );

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  const {
    data: discountList,
    isLoading,
    isFetched,
    isRefetching,
    refetch,
  } = useGetListData<IDiscount>('productCatalogDiscounts', {
    config: pager,
    filters: filterParams,
  });

  const { mutateAsync: doActivateDeactivate } =
    usePerformEntityAction<IDiscount>('productCatalogDiscounts', {
      meta: { showErrorToast: true },
    });

  const loading = isLoading || (isRefetching && !isFetched);
  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: discountList?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const onResetFilter = () => {
    setFilters(getDefaultFilter(FilterStateKeys.DISCOUNT_LIST));
    setPager(getDefaultPager(FilterStateKeys.DISCOUNT_LIST));
    tableRef.current?.resetFilters();
  };

  const { canCreateSettings } = useSettingsReadOnlyRule();

  const onSelectEdit = useCallback(
    (id: string, openInNewWindow = false) => {
      navigateOrOpenNewWindow(
        navigate,
        ROUTES.getDiscountEditRoute(id),
        openInNewWindow,
      );
    },
    [navigate],
  );

  const actionBodyTemplate = useCallback(
    (rowData: IDiscount) => {
      if (!canCreateSettings) {
        return null;
      }
      const { id, name, status } = rowData;
      const confirmProps = {
        title: `Are you sure you want to deactivate "${name}"?`,
        description:
          'Once deactivated, the code associated with this discount will no longer be redeemable. Customers who have already redeemed this discount will not be affected.',
      };
      const actions = [
        {
          title: 'Edit',
          enabled: true,
          action: () => onSelectEdit(id),
        },
        {
          title: 'Activate',
          enabled: status === DiscountStatusEnum.INACTIVE,
          action: () => doActivateDeactivate({ id, action: 'activate' }),
        },
        {
          title: 'Deactivate',
          color: 'danger',
          confirmProps: {
            ...confirmProps,
            yesButton: 'Deactivate',
          },
          enabled: status === DiscountStatusEnum.ACTIVE,
          action: () => doActivateDeactivate({ id, action: 'deactivate' }),
        },
      ];

      return <MDataTableActions actions={actions} />;
    },
    [canCreateSettings, doActivateDeactivate, onSelectEdit],
  );

  const accountIdFilterOptions = [
    {
      title: '',
      key: 'accountId',
      operator: FilterTypeOperator.TOGGLE,
      renderOptionContent: renderAccountIdToggleOptionContent,
      options: {
        falseValue: 'eq:null',
        trueValue: '',
      },
    },
    {
      title: 'Account Name',
      key: 'accountId',
      operator: FilterTypeOperator.EQUAL,
      renderOptionContent: (optionsProps: RenderTableFilterOptionProps) => (
        <AccountIdTableFilterOptionContent {...optionsProps} />
      ),
    },
  ];

  const accountEqualFilter = filters.find(
    (filter) =>
      filter.key === 'accountId' &&
      filter.operator === FilterTypeOperator.EQUAL,
  );
  const accountFilterValue = accountEqualFilter?.value as string | undefined;
  const { data: accountData } = useQuery({
    queryKey: ['useCustomSelectValue', accountFilterValue],
    queryFn: () => getAccountById(accountFilterValue as string),
    enabled: !!accountFilterValue,
    refetchOnWindowFocus: false,
  });

  const filterDefs = useMemo<ExternalFilterDef[]>(
    () => [
      {
        key: 'accountId',
        columnHeader: 'Account',
        isActive: (filter) => !!filter.value,
        getChipProps: (filter) => {
          if (filter.value === 'eq:null') {
            return { label: 'Custom Discounts' };
          }
          return { label: accountData?.accountName };
        },
      },
    ],
    [accountData?.accountName],
  );

  const {
    initialFilters,
    onFiltersChange,
    extraActiveFilters,
    externalFilters,
    setExternalFilters,
  } = useExternalFilters({
    filters,
    setFilters,
    pager,
    setPager,
    filterDefs,
  });

  const filterComponentReset = React.useRef<any>(null);
  const searchComponentReset = React.useRef<any>(null);

  const columnContext = useMemo<DiscountListColumnContext>(
    () => ({ actionBodyTemplate }),
    [actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    DISCOUNT_LIST_CATALOG,
    columnContext,
  );

  return (
    <MPageContainer enableAccountSearch>
      <PageHeader />
      <TableHeader
        model="Discount"
        showCreateNewBtn={!isTrulyEmptyList && canCreateSettings}
      >
        {!isTrulyEmptyList && (
          <MHStack gap="2">
            <MPageSearchInput
              placeholderKey="Name"
              value={searchTerm}
              onChange={setSearchTerm}
              count={discountList?.totalElements}
              resetSearch={searchComponentReset}
              mode="alwaysExpanded"
            />
            <RefreshTableButton refetch={refetch} />
            <MDataTableFilter
              filters={externalFilters}
              filterOptions={accountIdFilterOptions}
              setFilters={setExternalFilters}
              onResetFilter={onResetFilter}
              resetFilter={filterComponentReset}
            />
            <ExportEntityButton
              entity="discounts"
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
            />
          </MHStack>
        )}
      </TableHeader>
      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={discountList?.content ?? []}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={discountList?.totalElements}
        totalPages={discountList?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={initialFilters}
        onFiltersChange={onFiltersChange}
        extraActiveFilters={extraActiveFilters}
        onRowClick={(e, _tableRow, openInNewWindow) =>
          onSelectEdit(e.id, openInNewWindow)
        }
        isLoading={loading}
        emptyProps={{
          mainMessage: 'Looks like there are no discounts here.',
          smallMessage: null,
          btnLabel: canCreateSettings ? 'New Discount' : '',
          to: ROUTES.DISCOUNT_NEW,
        }}
        emptyFilterResultsProps={{
          btnLabel: 'Reset Filters',
        }}
        resetFilter={onResetFilter}
      />
    </MPageContainer>
  );
};

export default DiscountListPage;
